/**
 * Taking a chart out of the assistant: CSV, PNG, PDF.
 *
 * An employee reads an answer and then puts it into an email, a report, or a note on a customer
 * file — the same reason `CopyButton` exists. A chart is the one thing on the screen that cannot be
 * copied as text, so without this it is the only part of an answer that has to be screenshotted.
 *
 * ## Three formats, three different jobs
 *
 * * **CSV** is the figures, for anyone who wants to do arithmetic on them. It is the export that
 *   matters most and the only one that is lossless.
 * * **PNG** is the picture, for pasting into a message or a slide.
 * * **PDF** is the picture in a page, for attaching to something official.
 *
 * Each carries the chart's title and unit, and the CSV carries its sources — an exported figure
 * that has lost its attribution is the thing golden rule 3 exists to prevent, and it is far more
 * likely to be forwarded than the screen it came from.
 *
 * ## Why no library
 *
 * The same argument the renderer makes. PNG is `canvas.drawImage` over a serialised SVG; PDF is a
 * JPEG in a one-page document, which is about sixty lines of byte layout. jsPDF and its peers are a
 * rendering runtime and a font stack in a bank's dependency review, for a feature that is a
 * download button.
 *
 * ## What the browser makes awkward
 *
 * **A serialised SVG loses its styling.** `<img>` renders SVG in an isolated context with no access
 * to the document's stylesheets, so anything inherited — the font in particular — comes out as the
 * UA default. The renderer sets colour as attributes for its own reasons, which happens to survive;
 * the font is set explicitly here.
 *
 * **A canvas is tainted by a foreign image and cannot be read back.** Nothing here loads one: the
 * SVG is inlined as a data URL, so `toBlob` stays available.
 */

import type { ChartSpec } from "./chartSpec";

/** Rendered at twice the on-screen size, so the PNG is not soft on a high-density display. */
const EXPORT_SCALE = 2;
/** JPEG quality inside the PDF. High: this is a chart with thin axis lines, not a photograph. */
const PDF_JPEG_QUALITY = 0.92;
/** A4 at 72 dpi, in points. The page a bank prints on. */
const A4 = { width: 595.28, height: 841.89 };
const PDF_MARGIN = 48;

/** A filename stem from the chart's title: safe on every filesystem, and still recognisable. */
export function fileStem(spec: ChartSpec, fallback: string): string {
  const base = (spec.title || fallback)
    .replace(/[\\/:*?"<>|]/g, " ")
    .replace(/\s+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
  return base || fallback;
}

/** One CSV field, quoted when it has to be. */
function field(value: string): string {
  return /[",\n\r]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

/**
 * The chart as a spreadsheet.
 *
 * One row per category, one column per series, which is the orientation the chart is read in and
 * the one a pivot table expects. The title, unit and sources go above it as their own rows: a
 * figure without its unit is a number nobody can check, and one without its source is a claim.
 */
export function toCsv(
  spec: ChartSpec,
  labels: { title: string; unit: string; category: string; sources: string },
): string {
  const rows: string[][] = [];
  if (spec.title) rows.push([labels.title, spec.title]);
  if (spec.unit) rows.push([labels.unit, spec.unit]);
  if (spec.cite.length > 0) rows.push([labels.sources, spec.cite.join(", ")]);
  if (rows.length > 0) rows.push([]);

  rows.push([labels.category, ...spec.series.map((series) => series.label)]);
  spec.categories.forEach((category, index) => {
    rows.push([
      category,
      ...spec.series.map((series) => String(series.values[index] ?? "")),
    ]);
  });

  // CRLF and a BOM: Excel on a Windows workstation opens a UTF-8 CSV as Latin-1 without one, and
  // every Arabic category label arrives as mojibake. The employees this is for are on Windows.
  return (
    "﻿" + rows.map((row) => row.map(field).join(",")).join("\r\n") + "\r\n"
  );
}

/** The chart's SVG, standalone: sized, namespaced, and carrying the font it was drawn with. */
function serialise(svg: SVGSVGElement, width: number, height: number): string {
  const copy = svg.cloneNode(true) as SVGSVGElement;
  copy.setAttribute("xmlns", "http://www.w3.org/2000/svg");
  copy.setAttribute("width", String(width));
  copy.setAttribute("height", String(height));
  // Inherited in the page, absent in an isolated one. Without it every label falls back to the
  // UA serif and the export stops looking like the product.
  copy.setAttribute(
    "font-family",
    getComputedStyle(svg).fontFamily || "system-ui, sans-serif",
  );
  // A white ground. The figure's background comes from its container, and a transparent PNG
  // pasted into a document with a dark theme is a chart of invisible text.
  const ground = document.createElementNS("http://www.w3.org/2000/svg", "rect");
  ground.setAttribute("width", "100%");
  ground.setAttribute("height", "100%");
  ground.setAttribute("fill", "#ffffff");
  copy.insertBefore(ground, copy.firstChild);

  return new XMLSerializer().serializeToString(copy);
}

/** What the exported image needs that the on-screen figure keeps outside the SVG. */
export interface ExportCaption {
  title: string;
  /** Rendered under the title, as the figure does. */
  subtitle: string;
  direction: "ltr" | "rtl";
  /** The page's font stack, so the caption matches the axis labels inside the SVG. */
  fontFamily: string;
}

/** Height of the caption band above the chart, in canvas pixels. */
const CAPTION_HEIGHT = 30 * EXPORT_SCALE;
const CAPTION_PADDING = 12 * EXPORT_SCALE;

/**
 * Draw the chart onto a canvas at `EXPORT_SCALE`, with its title above it.
 *
 * The title and unit live in the figure's HTML, not in the SVG — so an export of the SVG alone is
 * a picture of unlabelled bars, which is exactly the thing that should not be forwarded to anyone.
 * They are drawn onto the canvas rather than injected into the SVG: `fillText` shapes Arabic and
 * honours `direction`, where an SVG `<text>` would need the bidi handling written by hand.
 */
async function toCanvas(
  svg: SVGSVGElement,
  caption?: ExportCaption,
): Promise<HTMLCanvasElement> {
  const box = svg.viewBox.baseVal;
  const width = box.width || svg.clientWidth;
  const height = box.height || svg.clientHeight;

  const source = serialise(svg, width, height);
  // A data URL rather than a blob URL: a blob URL is revoked on a timer in some browsers and the
  // image can lose its source mid-decode. Encoded as UTF-8 first, because `btoa` throws on the
  // Arabic in the labels.
  const encoded = btoa(
    String.fromCharCode(...new TextEncoder().encode(source)),
  );
  const image = new Image();
  image.src = `data:image/svg+xml;base64,${encoded}`;
  await image.decode();

  const band = caption?.title || caption?.subtitle ? CAPTION_HEIGHT : 0;

  const canvas = document.createElement("canvas");
  canvas.width = Math.round(width * EXPORT_SCALE);
  canvas.height = Math.round(height * EXPORT_SCALE) + band;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("this browser has no 2d canvas");
  context.fillStyle = "#ffffff";
  context.fillRect(0, 0, canvas.width, canvas.height);

  if (band > 0 && caption) {
    const rtl = caption.direction === "rtl";
    context.direction = caption.direction;
    context.textAlign = rtl ? "right" : "left";
    context.textBaseline = "alphabetic";
    const edge = rtl ? canvas.width - CAPTION_PADDING : CAPTION_PADDING;

    if (caption.title) {
      context.fillStyle = "#0f172a";
      context.font = `600 ${13 * EXPORT_SCALE}px ${caption.fontFamily}`;
      context.fillText(caption.title, edge, 15 * EXPORT_SCALE);
    }
    if (caption.subtitle) {
      context.fillStyle = "#64748b";
      context.font = `${10 * EXPORT_SCALE}px ${caption.fontFamily}`;
      context.fillText(caption.subtitle, edge, 26 * EXPORT_SCALE);
    }
  }

  context.drawImage(image, 0, band, canvas.width, canvas.height - band);
  return canvas;
}

export async function toPngBlob(
  svg: SVGSVGElement,
  caption?: ExportCaption,
): Promise<Blob> {
  const canvas = await toCanvas(svg, caption);
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) =>
        blob
          ? resolve(blob)
          : reject(new Error("the canvas produced no image")),
      "image/png",
    );
  });
}

function jpegFrom(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) =>
        blob
          ? resolve(blob)
          : reject(new Error("the canvas produced no image")),
      "image/jpeg",
      PDF_JPEG_QUALITY,
    );
  });
}

/**
 * A one-page PDF with the chart on it.
 *
 * Written out by hand. A PDF is a set of numbered objects, a cross-reference table giving each
 * one's byte offset, and a trailer pointing at the table — so the only real constraint is that the
 * offsets are counted in **bytes** rather than characters, which is why the body is assembled as
 * `Uint8Array` pieces and not as a string. A title with an Arabic character in it is two or three
 * bytes and one character, and counting characters puts every offset after it wrong; the file then
 * opens in some readers and not others, which is the worst way for this to fail.
 *
 * The image goes in as JPEG with `/DCTDecode`, which is the one encoding a PDF takes verbatim —
 * no compressor to write.
 */
export async function toPdfBlob(
  svg: SVGSVGElement,
  title: string,
  caption?: ExportCaption,
): Promise<Blob> {
  const canvas = await toCanvas(svg, caption);
  const jpeg = new Uint8Array(await (await jpegFrom(canvas)).arrayBuffer());

  // Fit the page's text box, preserving the aspect ratio.
  const available = A4.width - PDF_MARGIN * 2;
  const drawWidth = Math.min(available, canvas.width);
  const drawHeight = (canvas.height / canvas.width) * drawWidth;
  const x = (A4.width - drawWidth) / 2;
  const y = A4.height - PDF_MARGIN - drawHeight;

  const encoder = new TextEncoder();
  const parts: Uint8Array[] = [];
  const offsets: number[] = [];
  let length = 0;

  const push = (chunk: string | Uint8Array): void => {
    const bytes = typeof chunk === "string" ? encoder.encode(chunk) : chunk;
    parts.push(bytes);
    length += bytes.length;
  };
  const object = (n: number, body: string): void => {
    offsets[n] = length;
    push(`${n} 0 obj\n${body}\nendobj\n`);
  };

  push("%PDF-1.4\n");
  object(1, "<< /Type /Catalog /Pages 2 0 R >>");
  object(2, "<< /Type /Pages /Kids [3 0 R] /Count 1 >>");
  object(
    3,
    `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${A4.width} ${A4.height}] ` +
      "/Resources << /XObject << /Im0 4 0 R >> >> /Contents 5 0 R >>",
  );

  offsets[4] = length;
  push(
    `4 0 obj\n<< /Type /XObject /Subtype /Image /Width ${canvas.width} ` +
      `/Height ${canvas.height} /ColorSpace /DeviceRGB /BitsPerComponent 8 ` +
      `/Filter /DCTDecode /Length ${jpeg.length} >>\nstream\n`,
  );
  push(jpeg);
  push("\nendstream\nendobj\n");

  const content =
    `q\n${drawWidth.toFixed(2)} 0 0 ${drawHeight.toFixed(2)} ` +
    `${x.toFixed(2)} ${y.toFixed(2)} cm\n/Im0 Do\nQ\n`;
  object(
    5,
    `<< /Length ${encoder.encode(content).length} >>\nstream\n${content}endstream`,
  );
  // The title, for the reader's window chrome and for anyone searching a folder of these.
  object(6, `<< /Title (${title.replace(/([\\()])/g, "\\$1")}) >>`);

  const xref = length;
  const count = 7;
  let table = `xref\n0 ${count}\n0000000000 65535 f \n`;
  for (let n = 1; n < count; n += 1) {
    table += `${String(offsets[n] ?? 0).padStart(10, "0")} 00000 n \n`;
  }
  push(table);
  push(
    `trailer\n<< /Size ${count} /Root 1 0 R /Info 6 0 R >>\nstartxref\n${xref}\n%%EOF\n`,
  );

  return new Blob(parts as BlobPart[], { type: "application/pdf" });
}

/** Hand a file to the employee. */
export function download(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  // Revoked on the next frame rather than immediately: Safari has not started reading the blob
  // when `click` returns, and revoking synchronously gives an empty file.
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}
